import { getStorage, ref, uploadBytesResumable } from "firebase/storage";
import { app } from "./FirebaseConfig";

export const CLOUD_STORAGE_BASE_URL = 'https://firebasestorage.googleapis.com/v0/b/lifemap-418201.appspot.com/o/'

const storage = getStorage(app);

const RAW_VIDEO_PREFIX = 'videos/raw/';

/**
 * Upload a file to Firebase Cloud Storage with progress reporting.
 *
 * @param {string} filename                  Path to store the file at.
 * @param {File|Blob} file                   File or Blob to upload.
 * @param {(url:string)=>void} afterUploadComplete  Called with the final URL when the upload finishes.
 * @param {(progress:object)=>void} [onProgress]    Called repeatedly with progress info:
 *        { bytesTransferred, totalBytes, percent, state, speedBytesPerSec, etaSeconds }
 * @param {(err:Error)=>void} [onError]      Called if the upload fails.
 * @returns {import("firebase/storage").UploadTask} The upload task — useful for cancel/pause.
 */
export const storeFile = (filename, file, afterUploadComplete, onProgress, onError) => {
    const storageRef = ref(storage, filename);
    const storageUrl = CLOUD_STORAGE_BASE_URL + filename + '?alt=media';
    const metadata = file && file.type ? { contentType: file.type } : undefined;
    const uploadTask = uploadBytesResumable(storageRef, file, metadata);

    const startedAt = Date.now();

    uploadTask.on(
        'state_changed',
        (snapshot) => {
            if (!onProgress) return;
            const elapsedSec = Math.max(0.001, (Date.now() - startedAt) / 1000);
            const speedBytesPerSec = snapshot.bytesTransferred / elapsedSec;
            const remainingBytes = Math.max(0, snapshot.totalBytes - snapshot.bytesTransferred);
            const etaSeconds = speedBytesPerSec > 0 ? remainingBytes / speedBytesPerSec : Infinity;
            const percent = snapshot.totalBytes
                ? (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                : 0;
            onProgress({
                bytesTransferred: snapshot.bytesTransferred,
                totalBytes: snapshot.totalBytes,
                percent,
                state: snapshot.state, // 'paused' | 'running' | ...
                speedBytesPerSec,
                etaSeconds,
            });
        },
        (err) => {
            if (onError) onError(err);
            else console.error('storeFile error:', err);
        },
        () => {
            afterUploadComplete(storageUrl);
        }
    );

    return uploadTask;
};

/**
 * Upload a video to Firebase Cloud Storage at `videos/raw/{transcodeId}`,
 * which triggers the `transcodeVideo` Cloud Function. The function transcodes
 * to a browser-friendly H.264 MP4 and writes a `videoTranscodes/{transcodeId}`
 * Firestore doc that the editor's video node view subscribes to.
 *
 * Unlike `storeFile`, the completion callback fires when the *upload* is done
 * (not when the playable video is ready) and receives `{ transcodeId }` rather
 * than a playback URL — the URL doesn't exist yet, transcoding hasn't run.
 *
 * @param {string} transcodeId   Stable identifier for this video. Used as the
 *                               Storage object name and the Firestore doc id.
 *                               Must not contain '/'.
 * @param {File|Blob} file       The video file to upload.
 * @param {(info:{transcodeId:string})=>void} afterUploadComplete
 * @param {(progress:object)=>void} [onProgress]
 * @param {(err:Error)=>void} [onError]
 * @returns {import("firebase/storage").UploadTask}
 */
export const storeVideoForTranscoding = (transcodeId, file, afterUploadComplete, onProgress, onError) => {
    if (!transcodeId || transcodeId.includes('/')) {
        throw new Error(`Invalid transcodeId: ${transcodeId}`);
    }

    const storagePath = RAW_VIDEO_PREFIX + transcodeId;
    const storageRef = ref(storage, storagePath);
    // Pass contentType through so the function and any debugging URLs see the
    // browser's MIME guess instead of falling back to application/octet-stream.
    const metadata = file && file.type ? { contentType: file.type } : undefined;
    const uploadTask = uploadBytesResumable(storageRef, file, metadata);

    const startedAt = Date.now();

    uploadTask.on(
        'state_changed',
        (snapshot) => {
            if (!onProgress) return;
            const elapsedSec = Math.max(0.001, (Date.now() - startedAt) / 1000);
            const speedBytesPerSec = snapshot.bytesTransferred / elapsedSec;
            const remainingBytes = Math.max(0, snapshot.totalBytes - snapshot.bytesTransferred);
            const etaSeconds = speedBytesPerSec > 0 ? remainingBytes / speedBytesPerSec : Infinity;
            const percent = snapshot.totalBytes
                ? (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                : 0;
            onProgress({
                bytesTransferred: snapshot.bytesTransferred,
                totalBytes: snapshot.totalBytes,
                percent,
                state: snapshot.state,
                speedBytesPerSec,
                etaSeconds,
            });
        },
        (err) => {
            if (onError) onError(err);
            else console.error('storeVideoForTranscoding error:', err);
        },
        () => {
            afterUploadComplete({ transcodeId });
        }
    );

    return uploadTask;
};
